import { Link } from 'react-router'
import type { RefObject } from 'react'
import { getCharacterImageUrl, getCharacterImageSrcSet, type Character } from '@/shared/lib/characters'
import { UserMenu } from '@/shared/components/UserMenu'
import { useLocaleStore } from '@/shared/lib/i18n'
import { useScrollReveal } from './useScrollReveal'

const PARTICLES = [
  { top: '12%', left: '8%', size: 4, opacity: 0.55 },
  { top: '22%', left: '78%', size: 3, opacity: 0.4 },
  { top: '35%', left: '92%', size: 5, opacity: 0.35 },
  { top: '18%', left: '45%', size: 3, opacity: 0.3 },
  { top: '55%', left: '6%', size: 4, opacity: 0.45 },
  { top: '60%', left: '88%', size: 3, opacity: 0.38 },
  { top: '75%', left: '22%', size: 5, opacity: 0.28 },
  { top: '80%', left: '64%', size: 3, opacity: 0.35 },
  { top: '42%', left: '55%', size: 2, opacity: 0.25 },
  { top: '8%', left: '62%', size: 3, opacity: 0.32 },
]

function GoldParticles() {
  return (
    <>
      {PARTICLES.map((p, i) => (
        <div
          key={i}
          className="fixed pointer-events-none"
          style={{
            top: p.top,
            left: p.left,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(220,185,80,${p.opacity}) 0%, rgba(180,140,20,${p.opacity * 0.5}) 40%, transparent 100%)`,
            filter: `blur(${p.size * 0.8}px)`,
            zIndex: 1,
          }}
        />
      ))}
    </>
  )
}

function PersonCard({ char, index = 0 }: { char: Character; index?: number }) {
  const ref = useScrollReveal<HTMLDivElement>()
  const callHref = `/@${char.id}?call=1&from=home-card`
  const detailsHref = `/@${char.id}?from=home-details`

  return (
    <div
      ref={ref}
      style={{
        opacity: 0,
        transform: 'translateY(20px)',
        transition: `opacity 220ms ease-out ${index * 60}ms, transform 220ms ease-out ${index * 60}ms`,
      }}
    >
      <Link to={callHref} className="block no-underline group" aria-label={`${char.displayName} call`} style={{ outline: 'none' }}>
        <div style={{ position: 'relative', aspectRatio: '3/4', borderRadius: '12px', overflow: 'hidden', background: char.bgGradient, border: '1px solid rgba(190,160,60,0.12)', boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 40px rgba(160,130,40,0.07)' }}>
          <img
            src={getCharacterImageUrl(char.id)}
            srcSet={getCharacterImageSrcSet(char.id)}
            sizes="(max-width: 768px) 50vw, 25vw"
            alt={char.displayName}
            loading="lazy"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: char.imgPosition ?? 'center 15%', transition: 'transform 300ms ease-out' }}
            onMouseEnter={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1.04)' }}
            onMouseLeave={e => { (e.currentTarget as HTMLImageElement).style.transform = 'scale(1)' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.55) 100%)', pointerEvents: 'none' }} />
        </div>
      </Link>

      <div style={{ paddingTop: '10px', paddingLeft: '2px' }}>
        <Link to={callHref} className="no-underline" style={{ display: 'block' }}>
          <p style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: '18px', fontWeight: 300, fontStyle: 'italic', color: 'rgba(230,215,185,0.92)', lineHeight: 1.2, letterSpacing: '0.01em', marginBottom: '4px' }}>
            {char.displayName}
          </p>
        </Link>
        {char.subtitle ? (
          <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.30)', textTransform: 'uppercase' }}>
            {char.subtitle}
          </p>
        ) : char.role ? (
          <p style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.12em', color: 'rgba(255,255,255,0.30)', textTransform: 'uppercase' }}>
            {char.role}
          </p>
        ) : null}

        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <Link to={callHref} className="no-underline" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '40px', padding: '0 14px', borderRadius: '999px', border: '1px solid rgba(212,186,112,0.38)', background: 'rgba(212,186,112,0.12)', color: 'rgba(230,215,185,0.96)', fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em' }}>
            {char.fixedLanguage === 'ja' ? '話す' : 'Talk'}
          </Link>
          <Link to={detailsHref} className="no-underline" style={{ flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '40px', padding: '0 14px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.10)', background: 'transparent', color: 'rgba(255,255,255,0.68)', fontSize: '12px', fontWeight: 500, letterSpacing: '0.04em' }}>
            {char.fixedLanguage === 'ja' ? '詳細を見る' : 'View details'}
          </Link>
        </div>
      </div>
    </div>
  )
}

type Copy = {
  eyebrow: string
  headline: string
  sub: string
  gridTitle: string
  explainerTitle: string
  explainerBody: string
  primaryCta: string
  secondaryCta: string
}

export function WorldHomeSections({ copy, chars, hasChars, heroRef, locale, isCallOpen, isAuthenticated, isLoading, onOpenMiraCall, onJumpToSurfaces }: { copy: Copy; chars: Character[]; hasChars: boolean; heroRef: RefObject<HTMLDivElement | null>; locale: string; isCallOpen: boolean; isAuthenticated: boolean; isLoading: boolean; onOpenMiraCall: () => void; onJumpToSurfaces: () => void }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0B0A08', overflowX: 'hidden', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes callLayerIn {
          from { opacity: 0; transform: scale(0.985) translateY(14px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      <div className="fixed inset-0 pointer-events-none" style={{ background: `
            radial-gradient(ellipse 90% 60% at 50% 20%, rgba(160,130,40,0.10) 0%, rgba(80,60,20,0.04) 50%, transparent 75%),
            radial-gradient(ellipse 70% 45% at 50% 95%, rgba(180,140,20,0.16) 0%, rgba(100,75,10,0.06) 50%, transparent 80%)
          `, zIndex: 0 }} />
      <GoldParticles />

      <div aria-hidden={isCallOpen ? true : undefined} style={{ position: 'relative', zIndex: 10, transition: 'transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 320ms ease, opacity 220ms ease', transform: isCallOpen ? 'scale(0.985) translateY(10px)' : 'scale(1) translateY(0)', transformOrigin: '50% 0%', filter: isCallOpen ? 'blur(10px)' : 'none', opacity: isCallOpen ? 0.24 : 1, pointerEvents: isCallOpen ? 'none' : 'auto' }}>
        <header className="sticky top-0 z-50 flex items-center justify-between px-5 py-3" style={{ background: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <div data-brand="leka-ai" style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: '14px', fontWeight: 400, letterSpacing: '0.22em', color: 'rgba(212,186,112,0.75)', textTransform: 'uppercase' }}>LEKA AI</div>

          <div className="flex items-center gap-2">
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              {(['en', 'ja'] as const).map((lang) => (
                <button key={lang} onClick={() => useLocaleStore.getState().setLocale(lang)} style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', color: locale === lang ? 'rgba(255,255,255,0.70)' : 'rgba(255,255,255,0.22)', background: 'transparent', border: 'none', padding: '4px 5px', cursor: locale === lang ? 'default' : 'pointer', transition: 'color 200ms ease-out' }}>
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            {!isLoading && (isAuthenticated ? <UserMenu /> : <button onClick={() => { window.location.href = '/login' }} style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', transition: 'background 200ms ease-out' }} onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.10)' }} onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)' }} aria-label="Sign in">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            </button>)}
          </div>
        </header>

        <main className="flex-1 relative z-10">
          <section style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: 'clamp(48px, 10vw, 80px) 20px clamp(32px, 6vw, 56px)' }}>
            <p style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: '11px', fontWeight: 400, letterSpacing: '0.30em', color: 'rgba(255,255,255,0.30)', textTransform: 'uppercase', margin: '0 0 20px', animation: 'fadeInUp 500ms ease-out both', animationDelay: '0ms' }}>{copy.eyebrow}</p>
            <h1 style={{ fontFamily: "'Cormorant Garamond', 'Playfair Display', serif", fontSize: 'clamp(36px, 8vw, 58px)', fontWeight: 300, fontStyle: 'italic', color: '#D4BA70', letterSpacing: '-0.01em', lineHeight: 1.15, margin: '0 0 20px', whiteSpace: 'pre-line', animation: 'fadeInUp 500ms ease-out both', animationDelay: '80ms' }}>{copy.headline}</h1>
            <p style={{ fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: 400, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, maxWidth: '420px', margin: '0 0 32px', whiteSpace: 'pre-line', animation: 'fadeInUp 500ms ease-out both', animationDelay: '160ms' }}>{copy.sub}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', animation: 'fadeInUp 500ms ease-out both', animationDelay: '220ms' }}>
              <button onClick={onOpenMiraCall} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '14px 20px', borderRadius: '999px', border: '1px solid rgba(212,186,112,0.45)', background: 'rgba(212,186,112,0.14)', color: 'rgba(230,215,185,0.98)', textDecoration: 'none', fontSize: '14px', fontWeight: 600, letterSpacing: '0.02em', cursor: 'pointer', boxShadow: '0 10px 30px rgba(212,186,112,0.14)' }}>{copy.primaryCta}</button>
              <button onClick={onJumpToSurfaces} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '14px 20px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.10)', background: 'transparent', color: 'rgba(255,255,255,0.72)', fontSize: '14px', letterSpacing: '0.02em', cursor: 'pointer' }}>{copy.secondaryCta}</button>
            </div>
            <p style={{ margin: '14px 0 0', fontSize: '12px', color: 'rgba(255,255,255,0.32)', letterSpacing: '0.02em', animation: 'fadeInUp 500ms ease-out both', animationDelay: '280ms' }}>{locale === 'ja' ? 'ログインなしでそのまま始められる' : 'Start instantly, no login required'}</p>
          </section>

          <section style={{ maxWidth: '720px', margin: '0 auto', padding: '0 20px 32px' }}>
            <div style={{ border: '1px solid rgba(212,186,112,0.12)', background: 'rgba(255,255,255,0.02)', borderRadius: '16px', padding: '18px 18px 20px', boxShadow: '0 12px 40px rgba(0,0,0,0.18)' }}>
              <p style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '0.18em', color: 'rgba(212,186,112,0.62)', textTransform: 'uppercase', margin: '0 0 8px' }}>{copy.explainerTitle}</p>
              <p style={{ fontSize: '14px', lineHeight: 1.75, color: 'rgba(255,255,255,0.56)', margin: 0 }}>{copy.explainerBody}</p>
            </div>
          </section>

          <div style={{ maxWidth: '560px', margin: '0 auto', padding: '0 20px', borderTop: '1px solid rgba(255,255,255,0.05)', marginBottom: '32px' }} />

          <section id="persons" ref={heroRef} style={{ maxWidth: '560px', margin: '0 auto', padding: '0 16px 80px' }}>
            <p style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '0.20em', color: 'rgba(255,255,255,0.22)', textTransform: 'uppercase', marginBottom: '20px', textAlign: 'center' }}>{copy.gridTitle}</p>
            {hasChars && <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px 14px' }}>{chars.map((char, i) => <PersonCard key={char.id} char={char} index={i} />)}</div>}
          </section>

          {hasChars && (
            <section style={{ maxWidth: '720px', margin: '0 auto', padding: '0 20px 88px' }}>
              <div style={{ display: 'grid', gap: '12px' }}>
                {chars.map((char) => (
                  <div key={`${char.id}-seo-summary`} style={{ display: 'block', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '14px', padding: '14px 16px', background: 'rgba(255,255,255,0.015)' }}>
                    <p style={{ fontSize: '13px', fontWeight: 600, color: 'rgba(230,215,185,0.9)', margin: '0 0 4px' }}>{char.displayName} {char.role ? `· ${char.role}` : ''}</p>
                    <p style={{ fontSize: '13px', lineHeight: 1.65, color: 'rgba(255,255,255,0.48)', margin: 0 }}>{char.bio}</p>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                      <Link to={`/@${char.id}?call=1&from=home-summary`} className="no-underline" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '36px', padding: '0 14px', borderRadius: '999px', border: '1px solid rgba(212,186,112,0.32)', background: 'rgba(212,186,112,0.10)', color: 'rgba(230,215,185,0.94)', fontSize: '12px', fontWeight: 600 }}>
                        {char.fixedLanguage === 'ja' ? '話す' : 'Talk'}
                      </Link>
                      <Link to={`/@${char.id}?from=home-summary-details`} className="no-underline" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minHeight: '36px', padding: '0 14px', borderRadius: '999px', border: '1px solid rgba(255,255,255,0.10)', background: 'transparent', color: 'rgba(255,255,255,0.68)', fontSize: '12px', fontWeight: 500 }}>{char.fixedLanguage === 'ja' ? '詳細を見る' : 'View details'}</Link>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </main>

        <footer className="px-5 py-5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)', position: 'relative', zIndex: 10 }}>
          <div style={{ maxWidth: '560px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Link to="/privacy" className="no-underline" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', transition: 'color 200ms' }} onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.6)' }} onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.35)' }}>Privacy</Link>
              <Link to="/terms" className="no-underline" style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', transition: 'color 200ms' }} onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.6)' }} onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.35)' }}>Terms</Link>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <a href="https://x.com/lekaai" target="_blank" rel="noopener noreferrer" aria-label="X" style={{ color: 'rgba(255,255,255,0.35)', transition: 'color 200ms' }} onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.6)' }} onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.35)' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.262 5.633 5.901-5.633zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>{new Date().getFullYear()} Leka AI</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}
